'use client';

import { useEffect, useState } from 'react';

import { showToast } from '@/lib/ui/toast';

import { emitShareEvent, normalizeShareSurface, type ShareSurfaceInput } from './events';
import { copyToClipboard } from './share';

export interface CopyShareLinkToast {
  /** Toast ID from @/config/toast-ids. */
  id: string;
  /** Localized success message. */
  message: string;
  /** Optional duration override (ms). */
  duration?: number;
}

export interface UseCopyShareLinkOptions {
  /** Identifies where the share happened — required so analytics can
   *  attribute every share to its surface. Accepts the shorthand string
   *  for surfaces with no payload (e.g. `'referral_link'`) or the full
   *  object for surfaces with required fields (e.g.
   *  `{ kind: 'market', marketPubkey }`). */
  surface: ShareSurfaceInput;
  /** Optional toast to show on successful copy. Omit for surfaces that
   *  use a visual indicator (e.g. label flip "Copied!") instead. */
  toast?: CopyShareLinkToast;
  /** How long `copied` stays true after a successful copy. Default 2000ms. */
  copiedResetMs?: number;
}

export interface UseCopyShareLinkResult {
  /** Transient flag flipped to true for `copiedResetMs` after a successful
   *  copy. Lets callers swap a button label without managing state. */
  copied: boolean;
  /** Copy a URL. Returns true on success. Always emits the surface's share
   *  event with action='copy' on success and fires the optional toast. */
  copy: (url: string) => Promise<boolean>;
}

export function useCopyShareLink(options: UseCopyShareLinkOptions): UseCopyShareLinkResult {
  const surface = normalizeShareSurface(options.surface);
  const { toast } = options;
  const resetMs = options.copiedResetMs ?? 2000;
  const [copied, setCopied] = useState<boolean>(false);

  useEffect((): (() => void) | undefined => {
    if (!copied) return undefined;
    const timer = setTimeout((): void => {
      setCopied(false);
    }, resetMs);
    return (): void => {
      clearTimeout(timer);
    };
  }, [copied, resetMs]);

  async function copy(url: string): Promise<boolean> {
    const ok = await copyToClipboard(url);
    if (!ok) return false;
    setCopied(true);
    emitShareEvent(surface, 'copy');
    if (toast !== undefined) {
      showToast({
        id: toast.id,
        message: toast.message,
        variant: 'success',
        ...(toast.duration !== undefined ? { duration: toast.duration } : {}),
      });
    }
    return true;
  }

  return { copied, copy };
}
